import React, { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd"
import { useAuthUser, useIsAuthenticated } from "react-auth-kit"
import {
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  InputLabel,
  FormControl,
  Container,
  Grid,
} from "@mui/material"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import DeleteIcon from "@mui/icons-material/Delete"
import api from "../../../../utils/api"

interface QuestionData {
  id: number
  description: string
  eventId: any
  instanceId: any
  optionCount: number
  questionNum: number
}

function EditQuestionPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const auth = useAuthUser()
  const isAuthenticated = useIsAuthenticated()
  const eventId = auth()?.eventId?._path?.segments[1] ?? "null"

  const [selectedQuestionId, setSelectedQuestionId] = useState("")
  const [questions, setQuestions] = useState([] as QuestionData[])
  const [selectedType, setSelectedType] = useState("Single")
  const [description, setDescription] = useState("")
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [username, setUsername] = useState("")
  const [options, setOptions] = useState([{ option: "", optionNum: 1 }])
  const [status, setStatus] = useState("")

  // all instance ids from eventId
  useEffect(() => {
    if (eventId) {
      api
        .get(`/admin/events/${eventId}/voting-instances-ids`)
        .then((response) => {
          const instances = response.data
          const exists = instances.some((instance: any) => instance.id === id)
          if (!exists) {
            navigate(
              "/not-authorized?message=The%20voting%20instance%20you%20are%20trying%20to%20access%20does%20not%20exist."
            )
          }
        })
        .catch((error) => {
          console.error("Error fetching voting instances:", error)
        })

      api
        .get(`/admin/instances/${id}/status`)
        .then((response) => {
          const status = response.data.status
          if (status !== "pre-voting") {
            navigate(
              "/not-authorized?message=The%20voting%20instance%20you%20are%20trying%20to%20access%20is%20not%20open%20for%20voting."
            )
          }
          setStatus(status)
        })
        .catch((error) => {
          console.error("Error fetching voting instances:", error)
        })
    }
  }, [eventId, id, navigate])

  useEffect(() => {
    let isMounted = true // track if component is mounted

    const fetchQuestions = async () => {
      try {
        const response = await api.get(`/admin/instances/${id}/questions`)
        if (!isMounted) return

        const questionData = response.data
        if (!isMounted) return

        setQuestions(questionData)
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching questions:", error)
        }
      }
    }

    fetchQuestions()

    return () => {
      isMounted = false // cleanup
    }
  })

  useEffect(() => {
    setUsername(isAuthenticated() ? auth()?.username : "")
  }, [isAuthenticated, auth])

  const addOption = () => {
    setOptions([...options, { option: "", optionNum: options.length + 1 }])
  }

  // better if we just grab all the questions and store them in state, instead of fetching each time we select a question from dropdown
  const handleQuestionSelect = async (questionId: string) => {
    setSelectedQuestionId(questionId)
    try {
      const response = await api.get(`/admin/questions/${questionId}`)
      const questionData = response.data
      setDescription(questionData.description)
      setSelectedType(questionData.questionType)

      const sortedOptions = questionData.options.sort(
        (a: any, b: any) => a.optionNum - b.optionNum
      )
      setOptions(sortedOptions)
    } catch (error) {
      console.error("Error fetching question data:", error)
    }
  }

  const onDragEnd = (result: any) => {
    const { destination, source } = result
    if (!destination) return

    const reorderedOptions = [...options]
    const [movedItem] = reorderedOptions.splice(source.index, 1)
    reorderedOptions.splice(destination.index, 0, movedItem)

    // Update optionNum for each option
    const updatedOptions = reorderedOptions.map((option, index) => {
      return { ...option, optionNum: index + 1 }
    })

    setOptions(updatedOptions)
  }

  const handleSubmit = async () => {
    const emptyFields = options.some((option) => option.option.trim() === "")

    if (emptyFields) {
      setErrorMessage("Option fields cannot be empty")
      setOpenSnackbar(true)
      return
    }

    const updatedQuestionData = {
      description,
      questionType: selectedType,
      options,
      instanceId: id,
      username,
    }
    try {
      await api.put(
        `/admin/questions/${selectedQuestionId}`,
        updatedQuestionData
      )

      navigate(`/admin/event-admin/instance/${id}`)
    } catch (error) {
      console.error("There was an error:", error)
    }
  }

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = options.map((option, i) => {
      if (i === index) {
        return { ...option, option: value }
      }
      return option
    })
    setOptions(newOptions)
  }

  const handleSnackbarClose = () => {
    setOpenSnackbar(false)
    setErrorMessage("")
  }

  const removeOption = (index: number) => {
    const newOptions = [...options]
    newOptions.splice(index, 1)
    setOptions(newOptions)
  }

  const sortedOptions = [...options].sort((a, b) => a.optionNum - b.optionNum)

  return (
    <Container component="main">
      <Typography variant="h4" gutterBottom>
        Edit Question
      </Typography>
      <form onSubmit={(e) => e.preventDefault()}>
        <FormControl fullWidth margin="normal" required>
          {questions.length > 0 && (
            <InputLabel id="question-select-label">
              Select a Question
            </InputLabel>
          )}

          {questions.length > 0 ? (
            <Select
              label="Select a Question"
              labelId="question-select-label"
              value={selectedQuestionId}
              onChange={(e) => handleQuestionSelect(e.target.value)}
            >
              {status === "pre-voting" ? (
                questions?.map((question) => (
                  <MenuItem key={question.id} value={question.id}>
                    {question.description}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No questions available</MenuItem>
              )}
            </Select>
          ) : (
            <Typography variant="body1">No questions available</Typography>
          )}
        </FormControl>

        {selectedQuestionId && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="What is the question?"
                variant="outlined"
                value={description}
                required
                onChange={(e) => setDescription(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel required id="Question-Type-label">
                  Type
                </InputLabel>
                <Select
                  label="Type"
                  labelId="Question-Type-label"
                  id="type-select"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  required
                >
                  <MenuItem value="Single">Single</MenuItem>
                  <MenuItem value="Ranked">Ranked</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Options:
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Button variant="contained" onClick={addOption}>
                Add Option
              </Button>
            </Grid>

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="options">
                {(provided) => (
                  <Grid
                    item
                    xs={12}
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {sortedOptions.map((option, index) => (
                      <Draggable
                        key={option.optionNum}
                        draggableId={String(option.optionNum)}
                        index={option.optionNum - 1}
                      >
                        {(provided, snapshot) => (
                          <Grid
                            item
                            xs={12}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              ...provided.draggableProps.style,
                              display: "flex",
                              alignItems: "center",
                              boxShadow: snapshot.isDragging
                                ? "0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)"
                                : "none",
                            }}
                          >
                            <DragIndicatorIcon
                              style={{ cursor: "grab", marginRight: "10px" }}
                            />
                            <TextField
                              fullWidth
                              label={`Option ${option.optionNum}`}
                              variant="outlined"
                              value={option.option}
                              required
                              onChange={(e) =>
                                handleOptionChange(index, e.target.value)
                              }
                              style={{
                                marginBottom: "15px",
                              }}
                            />
                            <DeleteIcon
                              style={{
                                cursor:
                                  options.length > 1
                                    ? "pointer"
                                    : "not-allowed",
                                marginLeft: "10px",
                                background: options.length > 1 ? "red" : "grey",
                                borderRadius: "25%",
                              }}
                              onClick={() => {
                                if (options.length > 1) {
                                  // Replace removeOption with your actual function for removing an option
                                  removeOption(index)
                                }
                              }}
                            />
                          </Grid>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Grid>
                )}
              </Droppable>
            </DragDropContext>

            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                type="submit"
                onClick={handleSubmit}
                style={{ marginBottom: "20px", background: "#4caf50" }}
              >
                Save
              </Button>
            </Grid>

            <Snackbar
              open={openSnackbar}
              autoHideDuration={4000}
              onClose={handleSnackbarClose}
            >
              <Alert onClose={handleSnackbarClose} severity="error">
                {errorMessage}
              </Alert>
            </Snackbar>
          </Grid>
        )}
      </form>
    </Container>
  )
}

export default EditQuestionPage
